/**
 * HeadWithdrawModal 헬퍼 함수
 * 출금 모달 관련 유틸리티
 */

import {
    HeadWithdrawModalProps,
    HeadGetWithdrawableAmountResponseDto
} from '../types/headWithdraw.types';

// ======== 타입 정의 ========

/** 계좌 정보 타입 */
export type WithdrawAccountInfo = HeadWithdrawModalProps['accountInfo'];

/** 출금 가능 금액 정보 타입 */
export type WithdrawableAmount = HeadGetWithdrawableAmountResponseDto | null;

/** 수수료 계산 결과 타입 */
export interface FeeCalculation {
    transferFee: number;
    expectedAmount: number;
    totalDeduction: number; // 총 차감금액 (신청금액 + 수수료)
}

// ======== 스타일 상수 ========

/** 금액 입력 필드 스타일 */
export const AMOUNT_INPUT_STYLE: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 600,
    flex: 1
};

/** 계좌 정보 행 스타일 */
export const ACCOUNT_ROW_STYLE: React.CSSProperties = {
    marginBottom: '0.5rem'
};

// ======== 수수료 계산 헬퍼 ========

/**
 * 출금 수수료 계산
 * - 새 로직: 신청금액 전체가 출금되고, 수수료는 별도로 차감됨
 * - 총 차감금액 = 신청금액 + 수수료
 * @param numericAmount 출금 금액 (신청금액)
 * @param transferFee 출금수수료 (기본값 300원)
 * @returns 이체수수료, 출금예정금액, 총 차감금액
 */
export const calculateFees = (numericAmount: number, transferFee = 300): FeeCalculation => {
    if (numericAmount === 0) {
        return { transferFee, expectedAmount: 0, totalDeduction: 0 };
    }

    // 출금예정금액 = 신청금액 (수수료는 별도 차감)
    const expectedAmount = numericAmount;
    // 총 차감금액 = 신청금액 + 수수료
    const totalDeduction = numericAmount + transferFee;

    return {
        transferFee,
        expectedAmount,
        totalDeduction
    };
};

// ======== 계좌 정보 헬퍼 ========

/**
 * 출금 계좌 정보 문자열 생성
 * accountInfo를 우선 사용하고, 없으면 withdrawableAmount에서 가져옴
 * 은행명을 우선 사용하고, 없으면 은행코드를 사용
 */
export const getWithdrawAccountString = (
    accountInfo: WithdrawAccountInfo,
    withdrawableAmount: WithdrawableAmount
): string => {
    if (accountInfo?.withdrawBankCode || accountInfo?.withdrawAcctNum) {
        const bankName = (accountInfo as any)?.withdrawBankName || accountInfo.withdrawBankCode || '';
        const acctNum = accountInfo.withdrawAcctNum || '';
        const acctName = accountInfo.withdrawAcctName || '';
        return `[${bankName} ${acctNum} ${acctName}]`;
    }

    const bankName = withdrawableAmount?.withdrawBankName || 
                     withdrawableAmount?.bankName || 
                     withdrawableAmount?.withdrawBankCode || 
                     withdrawableAmount?.bankCode || '';
    const acctNum = withdrawableAmount?.withdrawAcctNum || withdrawableAmount?.acctNum || '';
    const acctName = withdrawableAmount?.withdrawAcctName || withdrawableAmount?.acctName || '';
    return `[${bankName} ${acctNum} ${acctName}]`;
};

/**
 * 입금 계좌 정보 문자열 생성
 * 은행명을 우선 사용하고, 없으면 은행코드를 사용
 */
export const getDepositAccountString = (
    accountInfo: WithdrawAccountInfo,
    withdrawableAmount: WithdrawableAmount
): string => {
    const bankName = (accountInfo as any)?.depositBankName || 
                     accountInfo?.depositBankCode || 
                     withdrawableAmount?.depositBankName || 
                     withdrawableAmount?.depositBankCode || '';
    const acctNum = accountInfo?.depositAcctNum || withdrawableAmount?.depositAcctNum || '';
    const acctName = accountInfo?.depositAcctName || withdrawableAmount?.depositAcctName || '';
    return `[${bankName} ${acctNum} ${acctName}]`;
};

/**
 * 입금 계좌 표시 여부 확인
 */
export const hasDepositAccount = (
    accountInfo: WithdrawAccountInfo,
    withdrawableAmount: WithdrawableAmount
): boolean => {
    return !!(
        accountInfo?.depositBankCode ||
        accountInfo?.depositAcctNum ||
        withdrawableAmount?.depositBankCode
    );
};
